import * as moment from 'moment';
import { isNullOrUndefined } from 'util';
import { Periods } from '../features/cash-flow/cash-flow.models';

export interface Month {
  name: string;
  value: number;
}

export interface Quarter {
  name: string;
  value: number;
}

export const MONTHS: Month[] = [
  {
    name: 'January',
    value: 0
  },
  {
    name: 'February',
    value: 1
  },
  {
    name: 'March',
    value: 2
  },
  {
    name: 'April',
    value: 3
  },
  {
    name: 'May',
    value: 4
  },
  {
    name: 'June',
    value: 5
  },
  {
    name: 'July',
    value: 6
  },
  {
    name: 'August',
    value: 7
  },
  {
    name: 'September',
    value: 8
  },
  {
    name: 'October',
    value: 9
  },
  {
    name: 'November',
    value: 10
  },
  {
    name: 'December',
    value: 11
  }
];

export const MONTHS_ESG: Month[] = [
  {
    name: 'June',
    value: 5
  },
  {
    name: 'December',
    value: 11
  }
];

export const QUARTERS: Quarter[] = [
  {
    name: 'Quarter 1',
    value: 1
  },
  {
    name: 'Quarter 2',
    value: 2
  },
  {
    name: 'Quarter 3',
    value: 3
  },
  {
    name: 'Quarter 4',
    value: 4
  }
];

export function getYearsDropdownValues(min?: moment.Moment, max?: moment.Moment) {
  if (!min || !max) {
    return [];
  }

  let difference = max.year() - min.year();

  if (difference === 0) {
    return [max.year()];
  }

  const years = [];

  for (let i = 0; difference >= 0; i++) {
    years.push(max.year() - i);
    difference--;
  }

  return years;
}

export function getYearsDropdownValuesCashFlow(period, min?: moment.Moment, max?: moment.Moment) {
  if (!min || !max) {
    return [];
  }
  let difference = max.year() - min.year();
  if (period === Periods.MONTH && difference === 0) {
    return [max.year()];
  }
  const years = [];
  for (let i = 0; difference >= 0; i++) {
    years.push(max.year() - i);
    difference--;
  }
  if (period === Periods.QUARTER) {
    const allowedMaxQuarter = max.quarter() - 1,
      quarters = [2, 5, 8, 11];
    if ((allowedMaxQuarter === 0 ||
      ((max.quarter() - min.quarter() === 0)
        && max.year() === min.year()))
      && !quarters.includes(max.month())) {
      years.shift();
    }
  }
  return years;
}
export function getMonthsDropdownValues(year?: number, min?: moment.Moment, max?: moment.Moment) {
  if (!year || !min || !max) {
    return [];
  }

  if (min.year() === year && max.year() === year) {
    return MONTHS.slice(min.month(), max.month() + 1);
  }

  if (min.year() === year) {
    return MONTHS.slice(min.month());
  }

  if (max.year() === year) {
    return MONTHS.slice(0, max.month() + 1);
  }

  return MONTHS;
}

export function getQuartersDropdownValues(year?: number, min?: moment.Moment, max?: moment.Moment) {
  if (!year || !min || !max) {
    return [];
  }

  /** Months on which quarters end, 0 based */
  const quarters = [2, 5, 8, 11];

  let allowedMaxQuarter = max.quarter() - 1;

  if (quarters.includes(max.month())) {
    allowedMaxQuarter = max.quarter();
  }

  if (min.year() === year && max.year() === year) {
    if (quarters.includes(max.month())
      && ((max.quarter() > min.quarter())
        || (max.quarter() - min.quarter() === 0))) {
      allowedMaxQuarter = max.quarter();
    } else {
      allowedMaxQuarter = max.quarter() - 1;
    }
    return QUARTERS.slice(min.quarter() - 1, allowedMaxQuarter);
  }

  if (min.year() === year) {
    return QUARTERS.slice(min.quarter() - 1);
  }

  if (max.year() === year) {
    return QUARTERS.slice(0, allowedMaxQuarter);
  }

  return QUARTERS;
}

export function formatDate(date: string, format = 'DD/MM/YYYY', utc = true): string {
  return utc ? moment.utc(date).format(format) : moment(date).format(format);
}

export function getEsgMonthsDropdownValues(year?: number, min?: moment.Moment, max?: moment.Moment) { // To DO

  if (!year || !min || !max) {
    return [];
  }

  if (min.year() === year && max.year() === year) {
    if (min.month() === 5 && min.month() === 11) {
      return MONTHS_ESG;
    } else if (min.month() === 11 && max.month() === 11) {
      return MONTHS_ESG.slice(1);
    } else if (min.month() === 5 && max.month() === 5) {
      return MONTHS_ESG.slice(0, 1);
    }
  }

  if (min.year() === year) {
    if (min.month() === 11) {
      return MONTHS_ESG.slice(1);
    }
  }

  if (max.year() === year) {
    if (max.month() === 5) {
      return MONTHS_ESG.slice(0, 1);
    }
  }
  return MONTHS_ESG;
}

export function getTaxDetailsYearsDropdownValues(minDate?: moment.Moment, maxDate?: moment.Moment, financialYearStartMonth?: any) {
  if (!minDate || !maxDate) {
    return [];
  }

  let difference = maxDate.year() - minDate.year();

  if ((difference === 0) && moment(maxDate).month() >= (financialYearStartMonth - 1)) {
    return [maxDate.year()];
  }
  if ((difference === 0) && moment(maxDate).month() < (financialYearStartMonth - 1)) {
    return [maxDate.year() - 1];
  }

  const years = [];
  let dateYear = maxDate.year();


  financialYearStartMonth = financialYearStartMonth - 1; // to get month number sync with moment

  if ((moment(minDate).month() < financialYearStartMonth) && (moment(maxDate).month() < financialYearStartMonth)) {
    dateYear = dateYear - 1;
  } else if ((moment(minDate).month() >= financialYearStartMonth) && (moment(maxDate).month() >= financialYearStartMonth)) {
    // no change is required for this
  } else if ((moment(minDate).month() >= financialYearStartMonth) && (moment(maxDate).month() < financialYearStartMonth)) {
    dateYear = dateYear - 1;
    difference = difference - 1;
  } else if ((moment(minDate).month() < financialYearStartMonth) && (moment(maxDate).month() >= financialYearStartMonth)) {
    difference = difference + 1;
  }
  for (let i = 0; difference >= 0; i++) {
    years.push(dateYear - i);
    difference--;
  }
  return years;

}

export function getStartDateOfMonthByMonthAndYear(month, year, format?) {
  // months start at index 0 in momentjs, so we subtract 1
  const startDate = moment([year, month - 1, 1]).format(format),
    // get the number of days for this month
    daysInMonth = moment(startDate).daysInMonth(),
    // we are adding the days in this month to the start date (minus the first day)
    endDate = moment(startDate).add(daysInMonth - 1, 'days').format(format);
  return startDate;
}

export function getEndDateOfMonthByMonthAndYear(month, year, format?) {
  // months start at index 0 in momentjs, so we subtract 1
  // const startDate = moment([year, month - 1, 1]).format(format);
  const startDate = moment([year, month, 1]).subtract(1, 'month').format(format),
    // get the number of days for this month
    daysInMonth = moment(startDate).daysInMonth(),
    // we are adding the days in this month to the start date (minus the first day)
    endDate = moment(startDate).add(daysInMonth - 1, 'days').format(format);
  return endDate;
}
